import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Bath, BedDouble, Car, Check, ExternalLink, FilePlus2, ImageOff, LayoutGrid, List,
  Ruler, Search, SlidersHorizontal, X,
} from "lucide-react";

import { searchComparables } from "../api/prospectingReportsApi";
import { recordKey } from "./CmaRecordSelection";
import ManualComparableModal from "./ManualComparableModal";
import { SKY, inputClass, money, monthYear } from "../marketUpdateTheme";

export const MAX_ADDED = 12;
// A busy suburb sells ~500 a year; the API's cap for this search.
const SEARCH_LIMIT = 300;
const THIN_SALES = 3;

const SORTS = [
  { key: "distance", label: "Distance (closest)" },
  { key: "newest", label: "Date (newest)" },
  { key: "price_desc", label: "Price (highest)" },
  { key: "price_asc", label: "Price (lowest)" },
];

const DATE_RANGES = [
  { months: 3, label: "Last 3 months" },
  { months: 6, label: "Last 6 months" },
  { months: 12, label: "Last 12 months" },
  { months: 24, label: "Last 2 years" },
];

const RADII = [
  { km: "", label: "Any distance" },
  { km: "0.5", label: "500 m" },
  { km: "1", label: "1 km" },
  { km: "2", label: "2 km" },
  { km: "5", label: "5 km" },
];

const TYPES = [
  { key: "", label: "Any type" },
  { key: "house", label: "House" },
  { key: "townhouse", label: "Townhouse" },
  { key: "apartment", label: "Apartment / unit" },
  { key: "villa", label: "Villa" },
  { key: "land", label: "Land" },
];

const SOURCE_LABEL = {
  crm: "Collings", homer: "Market feed", profile: "Property records", collings_data: "Saved profile", manual: "Added by you",
};

const num = (v) => (v === "" || v == null ? null : Number(v));

const MinMax = ({ label, value, onChange, unit, prefix }) => (
  <fieldset>
    <legend className="text-xs font-semibold text-gray-700 mb-1.5">{label}</legend>
    <div className="grid grid-cols-2 gap-2">
      {["min", "max"].map((edge) => (
        <div key={edge} className="relative">
          {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">{prefix}</span>}
          <input
            type="number"
            min="0"
            inputMode="numeric"
            aria-label={`${label} ${edge}`}
            placeholder={edge === "min" ? "Min" : "Max"}
            value={value[edge]}
            onChange={(e) => onChange({ ...value, [edge]: e.target.value })}
            className={`${inputClass} py-2 ${prefix ? "pl-6" : ""} ${unit ? "pr-8" : ""}`}
          />
          {unit && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">{unit}</span>}
        </div>
      ))}
    </div>
  </fieldset>
);

const Features = ({ row, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 tabular-nums ${className}`}>
    {row.bedrooms != null && <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" aria-hidden="true" />{row.bedrooms}<span className="sr-only"> bedrooms</span></span>}
    {row.bathrooms != null && <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" aria-hidden="true" />{row.bathrooms}<span className="sr-only"> bathrooms</span></span>}
    {row.car_spaces != null && <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5" aria-hidden="true" />{row.car_spaces}<span className="sr-only"> car spaces</span></span>}
    {row.land_size ? <span className="inline-flex items-center gap-1"><Ruler className="w-3.5 h-3.5" aria-hidden="true" />{Math.round(row.land_size)} m²</span> : null}
  </div>
);

// Homer's feed has no photos; ours come from the property records, which do
// not hold one for every house. Without one, the card links to the listing
// page so the agent can still see the property.
const Photo = ({ src, className, listingUrl }) => (
  src ? (
    <img src={src} alt="" loading="lazy" className={`${className} object-cover bg-gray-100`} />
  ) : (
    <div className={`${className} flex flex-col items-center justify-center gap-1.5 bg-gray-100 text-gray-400`}>
      <ImageOff className="w-6 h-6" aria-hidden="true" />
      {listingUrl && (
        <a href={listingUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-950 underline underline-offset-2">
          View listing photos <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </a>
      )}
    </div>
  )
);

/**
 * Steps 2 and 3: pick Surrounding Sales (kind=sold) or Current Listings
 * (kind=for_sale) for the report, "similar to how we have the CMA setup".
 * Laid out as Ignite's screen: filters left, photo cards centre, Added
 * Properties right.
 */
const MarketUpdatePropertyPicker = ({ report, kind, added, onChange }) => {
  const sold = kind === "sold";
  const [filters, setFilters] = useState({
    q: "",
    beds: { min: "", max: "" },
    baths: { min: "", max: "" },
    cars: { min: "", max: "" },
    price: { min: "", max: "" },
    land: { min: "", max: "" },
    radius: "",
    months: 12,
    type: "",
    suburbOnly: true,
    photosOnly: false,
  });
  const [sort, setSort] = useState("distance");
  const [view, setView] = useState("grid");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const set = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    let cancelled = false;
    // profiles=1: every agency's sales and listings from our property records,
    // not only Homer's and ours (Christian, 28 Oct 2026).
    const params = { kind, limit: SEARCH_LIMIT, profiles: 1 };
    if (filters.suburbOnly && report.suburb) params.suburb = report.suburb;
    if (report.full_address) params.subject_address = report.full_address;
    if (report.suburb) params.subject_suburb = report.suburb;
    if (report.state) params.state = report.state;
    if (report.latitude != null && report.longitude != null) {
      params.lat = report.latitude;
      params.lng = report.longitude;
    }
    if (filters.radius) params.radius_km = filters.radius;
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.type) params.property_type = filters.type;
    if (sold) params.sold_months = filters.months;
    [["beds", filters.beds], ["baths", filters.baths], ["cars", filters.cars], ["price", filters.price]].forEach(([key, range]) => {
      if (range.min !== "") params[`${key}_min`] = range.min;
      if (range.max !== "") params[`${key}_max`] = range.max;
    });

    setLoading(true);
    setFailed(false);
    const timer = setTimeout(async () => {
      try {
        const { data } = await searchComparables(params);
        if (cancelled) return;
        setRows(data?.results || []);
        setNote(data?.note || "");
      } catch (error) {
        if (!cancelled) { setRows([]); setFailed(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [kind, sold, report.suburb, report.state, report.latitude, report.longitude, report.full_address,
    filters.q, filters.type, filters.months, filters.radius, filters.suburbOnly,
    filters.beds, filters.baths, filters.cars, filters.price]);

  const addedKeys = useMemo(() => new Set(added.map(recordKey)), [added]);
  const subjectKey = (report.full_address || "").toLowerCase().split(",")[0].trim();

  const shown = useMemo(() => {
    const landMin = num(filters.land.min);
    const landMax = num(filters.land.max);
    const list = rows.filter((r) => {
      // The vendor's own house is not a comparable for itself.
      if (subjectKey && (r.full_address || "").toLowerCase().startsWith(subjectKey)) return false;
      if (landMin != null && !(r.land_size >= landMin)) return false;
      if (landMax != null && !(r.land_size <= landMax)) return false;
      if (filters.photosOnly && !r.image_url) return false;
      return true;
    });
    const by = {
      distance: (a, b) => (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity),
      newest: (a, b) => String(b.sold_date || b.listed_date || "").localeCompare(String(a.sold_date || a.listed_date || "")),
      price_desc: (a, b) => (Number(b.price) || 0) - (Number(a.price) || 0),
      price_asc: (a, b) => (Number(a.price) || Infinity) - (Number(b.price) || Infinity),
    }[sort];
    return [...list].sort(by);
  }, [rows, filters.land, filters.photosOnly, sort, subjectKey]);

  const full = added.length >= MAX_ADDED;
  const toggle = (row) => {
    const key = recordKey(row);
    if (addedKeys.has(key)) onChange(added.filter((r) => recordKey(r) !== key));
    else if (!full) onChange([...added, { ...row, kind }]);
  };

  const dateFor = (row) => (sold ? monthYear(row.sold_date) : monthYear(row.listed_date));
  const priceFor = (row) => money(row.price) || row.price_label || "Price withheld";

  const AddButton = ({ row, compact }) => {
    const isAdded = addedKeys.has(recordKey(row));
    return (
      <button
        type="button"
        onClick={() => toggle(row)}
        disabled={!isAdded && full}
        aria-pressed={isAdded}
        title={!isAdded && full ? `Up to ${MAX_ADDED} properties per report` : undefined}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg border text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55c5d0]/30 ${
          compact ? "px-3 py-1.5" : "w-full py-2"
        } ${isAdded ? "text-white border-transparent" : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"}`}
        style={isAdded ? { backgroundColor: SKY } : undefined}
      >
        {isAdded ? <><Check className="w-4 h-4" /> Added</> : "Add"}
      </button>
    );
  };

  const filterPanel = (
    <div className="space-y-4">
      <MinMax label="Bedrooms" value={filters.beds} onChange={(beds) => set({ beds })} />
      <MinMax label="Bathrooms" value={filters.baths} onChange={(baths) => set({ baths })} />
      <MinMax label="Garage" value={filters.cars} onChange={(cars) => set({ cars })} />
      <MinMax label="Price" value={filters.price} onChange={(price) => set({ price })} prefix="$" />
      <label className="block">
        <span className="block text-xs font-semibold text-gray-700 mb-1.5">Radius</span>
        <select className={`${inputClass} py-2`} value={filters.radius} onChange={(e) => set({ radius: e.target.value })}>
          {RADII.map((r) => <option key={r.km} value={r.km}>{r.label}</option>)}
        </select>
      </label>
      {sold && (
        <label className="block">
          <span className="block text-xs font-semibold text-gray-700 mb-1.5">Date range</span>
          <select className={`${inputClass} py-2`} value={filters.months} onChange={(e) => set({ months: Number(e.target.value) })}>
            {DATE_RANGES.map((d) => <option key={d.months} value={d.months}>{d.label}</option>)}
          </select>
        </label>
      )}
      <label className="block">
        <span className="block text-xs font-semibold text-gray-700 mb-1.5">Property type</span>
        <select className={`${inputClass} py-2`} value={filters.type} onChange={(e) => set({ type: e.target.value })}>
          {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </label>
      <MinMax label="Land size" value={filters.land} onChange={(land) => set({ land })} unit="m²" />
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={filters.photosOnly}
          onChange={(e) => set({ photosOnly: e.target.checked })} />
        <span>Only with photos</span>
      </label>
      {report.suburb && (
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={filters.suburbOnly}
            onChange={(e) => set({ suburbOnly: e.target.checked })} />
          <span>Only {report.suburb}<span className="block text-xs text-gray-500">Untick for the suburbs around it</span></span>
        </label>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[208px_minmax(0,1fr)] xl:grid-cols-[208px_minmax(0,1fr)_300px] gap-5">
      <aside className="hidden lg:block" aria-label="Filters">{filterPanel}</aside>

      <section className="min-w-0" aria-label={sold ? "Surrounding sales" : "Current listings"}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input
              type="search"
              value={filters.q}
              onChange={(e) => set({ q: e.target.value })}
              placeholder="Search property"
              aria-label="Search property"
              className={`${inputClass} pl-9`}
            />
          </div>
          <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <select className={`${inputClass.replace("w-full ", "")} w-auto py-2.5`} value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
            {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
          </select>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="radiogroup" aria-label="View">
            {[["grid", LayoutGrid, "Grid view"], ["list", List, "List view"]].map(([key, Icon, label]) => (
              <button key={key} type="button" role="radio" aria-checked={view === key} aria-label={label}
                onClick={() => setView(key)}
                className={`px-3 py-2.5 ${view === key ? "bg-gray-100 text-gray-900" : "bg-white text-gray-500 hover:text-gray-800"}`}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>

        {showFilters && <div className="lg:hidden mb-4 p-4 rounded-xl border border-gray-200 bg-white">{filterPanel}</div>}

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-xs text-gray-600" aria-live="polite">
            {loading ? "Searching…" : `${shown.length} ${sold ? "sale" : "listing"}${shown.length === 1 ? "" : "s"} found`}
          </p>
          <button type="button" onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:text-gray-950 underline underline-offset-4 decoration-gray-300">
            <FilePlus2 className="w-4 h-4" /> Can't find it? Add it yourself
          </button>
        </div>

        {loading ? (
          <div className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4" : "space-y-2"} aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`rounded-xl border border-gray-100 bg-white ${view === "grid" ? "h-72" : "h-20"} animate-pulse`} />
            ))}
          </div>
        ) : failed ? (
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-12 text-center">
            <p className="text-sm font-semibold text-gray-800">The property search did not load.</p>
            <p className="text-sm text-gray-500 mt-1">Change a filter to try again, or add a property yourself.</p>
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
            <p className="text-sm font-semibold text-gray-800">
              No {sold ? "sales" : "listings"} match these filters.
            </p>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              Try a longer date range, a wider radius or turning off "Only {report.suburb}". Our records are thinner
              than REA's, so adding a sale you know about is often the quickest fix.
            </p>
          </div>
        ) : view === "grid" ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4">
            {shown.map((row) => (
              <li key={recordKey(row)} className="flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
                <Photo src={row.image_url} listingUrl={row.listing_url} className="w-full aspect-[4/3]" />
                <div className="flex flex-col flex-1 p-3.5">
                  <p className="text-sm font-semibold text-gray-900 leading-snug">{row.full_address}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {[dateFor(row), row.distance_km != null ? `${row.distance_km.toFixed(1)} km away` : null].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-base font-bold text-gray-900 mt-2 tabular-nums">{priceFor(row)}</p>
                  <Features row={row} className="mt-1.5" />
                  <p className="text-[11px] text-gray-500 mt-2">
                    {[SOURCE_LABEL[row.source] || "Other source", row.agency_name].filter(Boolean).join(" · ")}
                  </p>
                  <div className="mt-auto pt-3"><AddButton row={row} /></div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            {shown.map((row) => (
              <li key={recordKey(row)} className="flex items-center gap-3 p-3">
                <Photo src={row.image_url} className="w-20 h-14 rounded-md flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{row.full_address}</p>
                  <p className="text-xs text-gray-600">
                    {[dateFor(row), row.distance_km != null ? `${row.distance_km.toFixed(1)} km` : null, SOURCE_LABEL[row.source]].filter(Boolean).join(" · ")}
                    {!row.image_url && row.listing_url && (
                      <> · <a href={row.listing_url} target="_blank" rel="noopener noreferrer"
                        className="font-semibold text-gray-700 hover:text-gray-950 underline underline-offset-2">View listing photos</a></>
                    )}
                  </p>
                  <Features row={row} className="mt-1" />
                </div>
                <p className="hidden sm:block text-sm font-bold text-gray-900 tabular-nums w-28 text-right">{priceFor(row)}</p>
                <AddButton row={row} compact />
              </li>
            ))}
          </ul>
        )}

        {note && !loading && <p className="text-xs text-gray-500 mt-4 max-w-2xl">{note}</p>}
      </section>

      <aside className="lg:col-span-2 xl:col-span-1" aria-label="Added properties">
        <div className="xl:sticky xl:top-4 rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-900">
              Added properties <span className="font-medium text-gray-500 tabular-nums">{added.length}/{MAX_ADDED}</span>
            </h2>
          </div>
          {added.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <FilePlus2 className="w-10 h-10 mx-auto text-gray-300" aria-hidden="true" />
              <p className="text-sm text-gray-600 mt-3">Add a property by choosing from the {view === "grid" ? "cards" : "list"}.</p>
              {!sold && <p className="text-xs text-gray-500 mt-1">Current listings are optional.</p>}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
              {added.map((row) => (
                <li key={recordKey(row)} className="flex items-center gap-3 px-3 py-2.5">
                  <Photo src={row.image_url} className="w-14 h-10 rounded flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 truncate">{row.full_address}</p>
                    <p className="text-xs text-gray-600 tabular-nums">{priceFor(row)}</p>
                  </div>
                  <button type="button" onClick={() => toggle(row)} aria-label={`Remove ${row.full_address}`}
                    className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {sold && added.length > 0 && added.length < THIN_SALES && (
            <p className="flex gap-2 mx-3 mb-3 p-2.5 rounded-lg bg-[#fff7e6] text-xs text-[#7a4b00]">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              Fewer than {THIN_SALES} sales can look thin to a vendor. Add more if you can.
            </p>
          )}
        </div>
      </aside>

      {showManual && (
        <ManualComparableModal
          defaultKind={kind}
          defaultSuburb={report.suburb}
          defaultState={report.state || "VIC"}
          onClose={() => setShowManual(false)}
          onAdd={(row) => {
            setShowManual(false);
            if (!full) onChange([...added, { ...row, kind, source: "manual", source_id: `manual-${Date.now()}` }]);
          }}
        />
      )}
    </div>
  );
};

export default MarketUpdatePropertyPicker;
